// SPDX-License-Identifier: AGPL-3.0-or-later

import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import styles from '@app/features/voice_scene/components/VoiceSceneTv.module.css';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import {
	createYouTubeTvPlayer,
	type YouTubeTvPlayer,
	youTubeEmbedUrl,
	youTubeThumbnailUrl,
} from '@app/features/voice_scene/video/YouTubeTvPlayer';
import type {VoiceWatch} from '@fluxer/constants/src/VoiceRoomWatch';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PlayIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const CLICK_TO_WATCH_DESCRIPTOR = msg({
	message: 'Click to watch',
	comment:
		'3D voice room TV: hint over the video thumbnail while the TV shows too small to play it; clicking brings the camera to the TV and the video starts.',
});

// The shared video on the TV's quad: YouTube's embed with its own controls and keys off and no pointer, so the room
// keeps its keys and wheel; the TV bar has the buttons. Kept on the shared time; while it is not playable (out of the
// TV cone, or too small for YouTube's player) it is paused and silent under its thumbnail. One per TV, keyed by
// video: the frame goes with it.
export const VoiceSceneTvVideo = observer(function VoiceSceneTvVideo({
	watch,
	playable,
	clickToWatch,
	label,
}: {
	watch: VoiceWatch;
	playable: boolean;
	// Over the thumbnail, the hint that a click brings the camera to the TV.
	clickToWatch: boolean;
	label: string;
}) {
	const {i18n} = useLingui();
	const frameRef = useRef<HTMLIFrameElement>(null);
	const playerRef = useRef<YouTubeTvPlayer | null>(null);
	const [src] = useState(() => youTubeEmbedUrl(watch.videoId, window.location.origin));
	const volume = Math.round(VoiceSceneSettings.tvVideoVolume * 100);
	const audible = !getEffectiveAudioState().effectiveDeaf;
	const captions = VoiceSceneSettings.tvCaptions;
	useEffect(() => {
		const player = createYouTubeTvPlayer({target: () => frameRef.current?.contentWindow ?? null});
		playerRef.current = player;
		const receive = (event: MessageEvent) => player.receive(event);
		window.addEventListener('message', receive);
		return () => {
			window.removeEventListener('message', receive);
			player.dispose();
			playerRef.current = null;
		};
	}, []);
	useEffect(() => {
		playerRef.current?.set({watch, playable, volume, audible, captions});
	}, [watch, playable, volume, audible, captions]);
	return (
		<div className={styles.video} data-flx="voice-scene.voice-scene-tv-video.video">
			{/* biome-ignore lint/a11y/useIframeTitle: project policy forbids the native title attribute (NoNativeTitleAttribute test); aria-label provides the accessible name */}
			<iframe
				ref={frameRef}
				src={src}
				className={styles.videoFrame}
				allow="autoplay; encrypted-media"
				sandbox="allow-scripts allow-same-origin"
				tabIndex={-1}
				aria-label={label}
				onLoad={() => playerRef.current?.frameLoaded()}
				data-flx="voice-scene.voice-scene-tv-video.video-frame"
			/>
			{playable ? null : (
				<>
					<img
						className={styles.videoThumbnail}
						src={youTubeThumbnailUrl(watch.videoId)}
						referrerPolicy="no-referrer"
						alt=""
						data-flx="voice-scene.voice-scene-tv-video.video-thumbnail"
					/>
					{clickToWatch ? (
						<div className={styles.videoHint} data-flx="voice-scene.voice-scene-tv-video.video-hint">
							<span className={styles.videoHintPill} data-flx="voice-scene.voice-scene-tv-video.video-hint-pill">
								<PlayIcon
									weight="fill"
									className={styles.videoHintIcon}
									aria-hidden="true"
									data-flx="voice-scene.voice-scene-tv-video.video-hint-icon"
								/>
								{i18n._(CLICK_TO_WATCH_DESCRIPTOR)}
							</span>
						</div>
					) : null}
				</>
			)}
		</div>
	);
});
